import { ReactNode, memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { EntityTypes } from 'constants/entities';
import { getImageFromFiles } from 'core/selectors/product/scene/scenePreview';
import { update } from 'core/actions/admin/data';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowsRightLeftIcon,
  Bars3BottomLeftIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { selectCourseNode } from 'core/selectors/admin/courseNode';
import { selectModule, selectModuleActionsLength } from 'core/selectors/product/module';
import { Resolutions } from 'types/domain/image';
import TextEditor, { TextType } from 'app/components/TextEditor';
import { ICourseNodeDAO, IModuleCourseNode } from 'types/domain/course';
import { millisToMinutesAndSeconds } from 'utils/convertTime';
import { CourseNodeBasicStatus } from '../Status';
import { TileMenu } from 'app/components/TileMenu';
import { updateState } from 'core/actions/product/state';
import { selectCanEdit } from 'core/selectors/admin/coursePermission';
import { IModuleDAO, IModuleDetailDAO, ModuleStatus } from 'types/domain/module';
import { Modals, setModal } from 'core/slices/modals';
import { selectIsDefaultCourse } from 'core/selectors/admin/course';
import { SECTIONS } from 'constants/sections';
import { selectModulePreviewOptions } from 'core/selectors/admin/modulePreviewOptions';
import { ModuleTypeColor, ModuleTypeIcon } from '../Constants/module';
import { selectAction } from 'core/selectors/product/action/action';
import { IActionBasic } from 'types/domain/action';
import { Role } from 'types/domain/role';

export const CourseNodeModule = memo(
  ({
    id,
    className,
    editMessage,
    mod,
  }: {
    id: number;
    mod?: IModuleDAO;
    className?: string;
    editMessage?: (e) => void;
  }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const courseNode: ICourseNodeDAO = useAppSelector(state => selectCourseNode(state, id));
    const module: IModuleDAO | IModuleDetailDAO = useAppSelector(
      state => mod || selectModule(state, courseNode?.moduleId),
    );
    //@ts-ignore
    const action: IActionBasic = useAppSelector(state => selectAction(state, module?.actionId));
    const images = useAppSelector(state => mod || selectModulePreviewOptions(state, courseNode?.moduleId));
    const actionsLength: number = useAppSelector(state => selectModuleActionsLength(state, courseNode?.moduleId));
    const canEdit: boolean = useAppSelector(selectCanEdit);
    const role: Role = useAppSelector(state => state.session.role);
    const courseId = useAppSelector(state => state.state[EntityTypes.COURSE]);
    const isDefault = useAppSelector(selectIsDefaultCourse);
    const img = useMemo(
      () =>
        getImageFromFiles(
          module?.image ? module?.image?.files : images.length && images[0] ? images[0].files : null,
          Resolutions.SMALL_512,
        ),
      [images, module?.image],
    );
    const onClick = useCallback(
      e => {
        if (e.target.id === 'container') {
          if (courseNode) dispatch(updateState({ type: EntityTypes.COURSE_NODE, id: courseNode.id }));
          navigate(`/${SECTIONS.COURSE}/${courseId}/module/${module.id}`, { replace: false });
        }
      },
      [courseId, courseNode, dispatch, module?.id, navigate],
    );

    const onChangeModule = useCallback(
      val => dispatch(update({ type: EntityTypes.MODULE, entity: { id: courseNode?.moduleId, ...val } })),
      [],
    );

    const style = useMemo(() => {
      return {
        transform: CSS.Transform.toString(transform),
        transition,
      };
    }, [transform, transition]);

    if (!courseNode && !module) return null;

    const iconClass = 'w-10 h-10 rounded p-2 my-0.5 cursor-pointer hover:text-brand bg-black/40';

    if (!module) return <></>;

    return (
      <div style={style} className="w-full hd-full md:w-auto m-2" ref={setNodeRef}>
        <div
          id={'container'}
          onClick={onClick}
          className={` ${className} flex flex-col w-full md:w-[320px] h-[320px] justify-center border-2 border-gray-400/20  items-center rounded-lg cursor-pointer relative`}
        >
          <div
            id={'image'}
            style={{
              backgroundImage: img ? `url(${img})` : null,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              transform: module?.image?.rotation ? `rotate(${module.image.rotation}deg)` : null,
            }}
            className="m-2 w-full h-full rounded-lg module-container overflow-hidden absolute z-0 pointer-events-none flex justify-center items-start"
          >
            {!img ? (
              action && action.text ? (
                <div className="flex justify-start items-center scale-75  w-full h-full mx-4 my-4">
                  <TextEditor
                    type={TextType.HTML}
                    textId={action.textId}
                    readOnly
                    parentId={action.id}
                    parentType={EntityTypes.ACTION}
                  />
                </div>
              ) : (
                <Bars3BottomLeftIcon className="w-20 text-white/50 pointer-events-none mt-20 " />
              )
            ) : null}
          </div>
          {canEdit ? (
            <div className="w-full flex justify-end items-center text-white p-1 z-1 relative mt-0 mb-auto">
              {courseNode && !courseNode.nodeId && !isDefault && (
                <ArrowsRightLeftIcon
                  {...listeners}
                  {...attributes}
                  className={`${iconClass} hidden md:block ml-2 mr-auto`}
                />
              )}
              {module.public ? (
                <span className="text-white rounded-lg py-1 px-2 text-sm bg-black mx-1">Public</span>
              ) : null}
              {isDefault && (
                <>
                  {role === Role.SUPER_ADMIN && module.public ? (
                    <div
                      onClick={e =>
                        onChangeModule({
                          status: module.status === ModuleStatus.TEMPLATE ? ModuleStatus.ACTIVE : ModuleStatus.TEMPLATE,
                        })
                      }
                      className={`flex justify-center items-center  mx-2  rounded-lg p-2 ${
                        module.status === ModuleStatus.TEMPLATE
                          ? 'border border-white text-white bg-brand'
                          : 'bg-black/40'
                      }`}
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </div>
                  ) : null}

                  {/* <div
                    onClick={e => onChange({ home: courseNode.home ? 0 : 1 })}
                    className={`flex justify-center items-center  mx-2  rounded-lg p-2 ${
                      courseNode.home ? 'border border-brand text-white bg-brand' : 'bg-black/40'
                    }`}
                  >
                    <HomeIcon className="w-4 h-4" />
                  </div> */}
                </>
              )}

              <TileMenu
                buttonClassName=" bg-black/40 rounded-lg p-1 ml-auto mr-2 flex justify-center items-center"
                className=""
                dark={true}
                type={courseNode ? EntityTypes.COURSE_NODE : EntityTypes.MODULE}
                id={courseNode ? courseNode?.id : module?.id}
                onShare={() => dispatch(setModal({ type: Modals.SHARE_MODULE, data: { id: module?.id } }))}
              />
            </div>
          ) : null}

          <div className="flex flex-col mt-auto w-full justify-center items-center relative z-1 text-lg bg-white rounded-b-lg p-2 text-gray-800">
            <div className="flex w-full justify-end">
              <div className="flex ml-2 mr-auto items-center">
                <div
                  className={`w-6 h-6 flex justify-center items-center  rounded-full ${ModuleTypeColor(module.type)}`}
                >
                  {ModuleTypeIcon(module.type, 'w-4 h-4 text-black')}
                </div>
                {actionsLength ? <span className="text-xs ml-2 text-left ">{actionsLength}</span> : null}
              </div>

              {!isDefault && courseNode.courseProgressCount > 0 ? (
                <span className="text-xs mr-2 flex justify-center items-center">
                  <UserIcon className="w-3 h-3 mx-1" />
                  {courseNode.courseProgressCount}
                </span>
              ) : null}

              {courseNode.sessionAttemptCount > 0 ? (
                <span className="text-xs mr-2 flex justify-center items-center">
                  <UsersIcon className="w-3 h-3 mx-1" /> {courseNode.sessionAttemptCount}
                </span>
              ) : null}

              {canEdit && courseNode ? (
                <CourseNodeBasicStatus courseNode={courseNode} full={false} className=" text-sm" />
              ) : null}
            </div>

            <TextEditor
              parentType={EntityTypes.MODULE}
              parentId={courseNode?.moduleId || module?.id}
              textId={module?.textId}
              readOnly={!canEdit}
              placeholder={module?.name}
              type={TextType.SHORT_TEXT}
              className="w-full"
              //onClick={e => e.stopPropagation()}
            />
            {/* {courseNode && (
              <CourseModuleNodeStats
                basic={false}
                courseNode={courseNode}
                className="flex flex-col text-gray-200 w-full text-xs my-2 p-2 "
                statClassName="flex items-center  mx-2"
              />
            )} */}
          </div>
        </div>
      </div>
    );
  },
);

export function CourseModuleNodeStats({
  basic,
  courseNode,
  className,
  statClassName,
}: {
  basic: boolean;
  courseNode: ICourseNodeDAO | IModuleCourseNode;
  className?: string;
  statClassName?: string;
}) {
  const iconClass = 'w-4 h-4';

  const completeRate = courseNode.sessionAttemptCount
    ? ((courseNode.sessionAttemptCompleteCount / courseNode.sessionAttemptCount) * 100).toFixed(0)
    : 0;
  const progress = courseNode.sessionAttemptCount
    ? ((courseNode.sessionAttemptTotalProgress / courseNode.sessionAttemptCount) * 100).toFixed(0)
    : 0;

  return (
    <div className={className}>
      {courseNode.sessionAttemptCount ? (
        <>
          <span className="ml-1 mt-auto">{`Complete: ${courseNode.sessionAttemptCompleteCount} / ${courseNode.sessionAttemptCount}`}</span>

          {!basic && (
            <>
              <div className="relative w-full mb-2 h-2 bg-howio-700 rounded p-0 overflow-hidden">
                <div
                  style={{ width: `${completeRate}%`, backgroundColor: 'rgb(82,171,65)' }}
                  className={`absolute h-2 z-1 rounded`}
                ></div>
              </div>
              <div>
                <span className="ml-1">{`Progress: ${progress}%`}</span>
                <div className="relative w-full mb-2 h-2 bg-howio-700 rounded p-0 overflow-hidden">
                  <div style={{ width: `${progress}%` }} className={`bg-yellow-500 absolute h-2 z-1 rounded`}></div>
                </div>
              </div>
            </>
          )}

          <div className="flex flex-wrap mt-auto">
            <CourseModuleNodeStat
              label={'Time'}
              className={statClassName}
              value={`${millisToMinutesAndSeconds(
                courseNode.sessionAttemptTotalTime / courseNode.sessionAttemptCount,
              )}`}
              icon={<ClockIcon className={iconClass} />}
            />
            {courseNode.variableTotalScore !== 0 && (
              <CourseModuleNodeStat
                label={'Score'}
                className={statClassName}
                value={`${(courseNode.variableTotalScore / courseNode.variableCount).toFixed(1)}`}
                icon={<ClockIcon className={iconClass} />}
              />
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function CourseModuleNodeStat({
  label,
  value,
  icon,
  className,
}: {
  label: string;
  value: number | string;
  icon: ReactNode;
  className: string;
}) {
  return (
    <div className={className}>
      {icon} <span className="ml-1 mr-auto">{label}</span> <span className="ml-2">{value}</span>
    </div>
  );
}
